import * as DateFnsNs from 'date-fns'
import lodash from 'lodash'
import { type MRT_ColumnSizingState, type MRT_VisibilityState } from 'material-react-table'
import ConfirmExportDataLimitPresenter from 'src/main/ConfirmExportDataLimit.presenter'
import { excludedExportDocIds } from 'src/main/Main.scopes'
import { AppStorage } from 'src/utils'
import { downloadBlobAsFile } from 'src/utils/download-utils'
import * as lang from 'src/utils/lang'
import { Logger, Presenter } from 'wdc-cube'

import {
    TheConsumerMarketService,
    type ColumnFilter,
    type ColumnSort,
    type EmpresaFilter
} from '../the-consumer-market/tcm_service'

import { TheConnectionKeys } from './tc_keys'
import { type TheConnectionPresenter } from './tc_presenter'
import { TableEmpresaScope } from './tc_scopes'

type PaginationState = {
    pageIndex: number
    pageSize: number
}

const LOG = Logger.get('TcPresenterForEmpresasListagem')

const service = TheConsumerMarketService.singleton()
const localStorage = AppStorage.singleton()

export default class TcPresenterForEmpresasListagem extends Presenter<TableEmpresaScope, TheConnectionPresenter> {
    constructor(owner: TheConnectionPresenter) {
        super(owner, owner.scope.listagemTabs.empresasListagem)
        this.__parent = owner
        this.__confirmExportDataPresenter = new ConfirmExportDataLimitPresenter(
            owner.app,
            this.scope.exportDataLimitScope
        )
    }

    private readonly __parent: TheConnectionPresenter
    private readonly __confirmExportDataPresenter: ConfirmExportDataLimitPresenter

    private __pendingPushConnectionId?: number

    async initialize() {
        this.scope.update = this.update
        this.scope.caption = 'Detalhes das empresas'
        this.scope.onSelectionChange = this.action(this.__onEmpresaRowClicked)
        this.scope.onFiltrationChange = this.action(this.__onFiltrationChange)
        this.scope.onPaginationChange = this.action(this.__onPaginationChange)
        this.scope.onSortingChange = this.action(this.__onSortingChange)
        this.scope.onDownloadClick = this.action(this.__onDownloadClicked)
        this.scope.setColumnSizing = this.action(this.__onSetColumnSizing)
        this.scope.onColumnsVisibilityChange = this.action(this.__onSetColumnsVisibility)
        this.scope.onCopyCnpj = this.action(this.__onCopyCnpj)
        this.scope.onOpenCorporateRelations = this.action(this.__onOpenCorporateRelations)
        this.scope.onCrmExport = this.action(this.__onCrmExport)

        this.scope.exportDataLimitScope.onYesNoClicked = this.action(this.__onYesNoConfirmExport)
        this.scope.exportDataLimitScope.canExcludeAlreadyExported = true

        this.__confirmExportDataPresenter.initialize()

        this.__initializeColumnsVisibility()

        this.__loadCrmExportOptions().catch(LOG.caught)

        LOG.debug('ready')
    }

    override release(): void {
        this.__confirmExportDataPresenter.release()
        super.release()
    }

    clear() {
        this.scope.pageIndex = 0
        this.scope.pageSize = 100
        this.scope.dataset = []
        this.scope.datasetVersion++
        this.scope.rowCount = 0
        this.scope.checkedRowCount = 0
        this.scope.loaded = true
        this.scope.update()
    }

    async fetch(
        filter: EmpresaFilter,
        cfg?: {
            paginationState?: PaginationState
            sorting?: ColumnSort[]
            columnFilters?: ColumnFilter[]
        }
    ) {
        let { paginationState, sorting, columnFilters } = cfg ?? {}
        this.scope.loaded = false
        try {
            if (!columnFilters) {
                columnFilters = this.scope.filters
            }

            if (!paginationState) {
                paginationState = {
                    pageIndex: 0,
                    pageSize: this.scope.pageSize
                }
            }

            if (!sorting) {
                sorting = this.scope.sorting
            }

            let search_after: unknown
            if (paginationState.pageIndex > 0) {
                const lastRow = this.scope.dataset[this.scope.dataset.length - 1]
                if (lastRow) {
                    search_after = [lastRow.faturamento_estimado, lastRow.func_qtd, lastRow.cnpj, lastRow.score]
                }
            }

            const resp = await service.fetch({
                listagem_empresas: {
                    sorting,
                    columnFilters,
                    search_after,
                    pageSize: paginationState.pageSize
                },
                filter: filter
            })

            this.scope.pageIndex = paginationState.pageIndex
            this.scope.pageSize = paginationState.pageSize
            this.scope.sorting = sorting

            const dataset = resp.listagem_empresas ?? []
            for (let i = 0; i < dataset.length; i++) {
                const row = dataset[i]
                if (lodash.isString(row.dta_inicio_atividade)) {
                    row.dta_inicio_atividade = DateFnsNs.parseISO(row.dta_inicio_atividade)
                }
                if (lodash.isString(row.dta_situacao_cadastral)) {
                    row.dta_situacao_cadastral = DateFnsNs.parseISO(row.dta_situacao_cadastral)
                }
            }
            LOG.debug(`dataset.length=${dataset.length}`)

            if (resp.listagem_empresas_agg) {
                this.scope.rowCount = resp.listagem_empresas_agg['pj-count'].value | 0
                this.scope.total.func_qtd = resp.listagem_empresas_agg['pj-func-qtd-emp-sum'].value
                this.scope.total.faturamento_estimado = resp.listagem_empresas_agg['pj-fatu-est-emp-sum'].value
                this.scope.total.faturamento_estimado_grupo = resp.listagem_empresas_agg['pj-fatu-est-grp-sum'].value
            }
            this.scope.dataset = dataset
            this.scope.datasetVersion++
        } finally {
            this.scope.loaded = true
            this.scope.update()
        }
    }

    private __initializeColumnsVisibility() {
        const savedVisibleColumns = localStorage.tcCompaniesVisibleColumns
        if (savedVisibleColumns) {
            this.scope.columnVisibility = JSON.parse(savedVisibleColumns) as MRT_VisibilityState
        } else {
            this.scope.columnVisibility = DEFAULT_COLUMNS_VISIBILITY
        }
    }

    private async __onEmpresaRowClicked(selectionMap: Map<string, boolean>) {
        for (const row of this.scope.dataset) {
            row.selected = selectionMap.get(row.cnpj) === true
        }
        this.scope.update()
    }

    async __onFiltrationChange(columnFilters: ColumnFilter[]) {
        this.scope.filters = columnFilters
        await this.fetch(this.__parent.buildTableFilter(), { columnFilters })
    }

    async __onPaginationChange(pageIndex: number, pageSize: number) {
        if (pageSize !== this.scope.pageSize) {
            pageIndex = 0
        }
        await this.fetch(this.__parent.buildTableFilter(), {
            paginationState: { pageIndex, pageSize }
        })
    }

    async __onSortingChange(sorting: ColumnSort[]) {
        await this.fetch(this.__parent.buildTableFilter(), { sorting })
    }

    async __onSetColumnSizing(columns: MRT_ColumnSizingState) {
        this.scope.columnSizing = columns
    }

    async __onSetColumnsVisibility(columns: MRT_VisibilityState) {
        this.scope.columnVisibility = columns
        localStorage.tcCompaniesVisibleColumns = JSON.stringify(columns)
    }

    async __onDownloadClicked(columns: string[]) {
        this.scope.exportColumns = columns

        this.scope.exportDataLimitScope.isChecking = true
        this.scope.update()
        try {
            const limitExportData = await service.checkLimitExportData(this.__parent.buildTableFilter(), 'empresas')
            await this.scope.exportDataLimitScope.onCheckLimitExportData(limitExportData)
        } finally {
            this.scope.exportDataLimitScope.isChecking = false
        }
    }

    protected async __onYesNoConfirmExport(yes: boolean) {
        LOG.debug(`__onYesNoConfirmExport: yes=${yes}`)
        if (yes) {
            if (this.__pendingPushConnectionId != null) {
                await this.__executePush(this.__pendingPushConnectionId, true)
            } else {
                await this.__executeDownload()
            }
        }
        this.__pendingPushConnectionId = undefined
        this.scope.exportDataLimitScope.open = false
        this.scope.update()
    }

    async __executeDownload() {
        try {
            this.scope.working = true
            this.scope.exportDataLimitScope.isDownloading = true

            const blob = await service.downloadXlsx(
                this.__parent.buildTableFilter(),
                this.scope.exportColumns,
                'empresas',
                this.scope.exportDataLimitScope.qttExportRecords,
                this.__parent.app.compliance,
                excludedExportDocIds(this.scope.exportDataLimitScope)
            )

            downloadBlobAsFile(blob, 'tc-empresa.xlsx')
        } finally {
            this.scope.exportDataLimitScope.isDownloading = false
            this.scope.working = false
        }
    }

    async __onCopyCnpj() {
        if (!navigator.clipboard) {
            this.__parent.app.alert('error', 'Aviso', 'Área de transferência não disponível')
            return
        }

        const cnpjArray = this.__collectSelectedCnpjs()
        await navigator.clipboard.writeText(cnpjArray.join(','))
    }

    async __onOpenCorporateRelations() {
        const cnpjArray = this.__collectSelectedCnpjs()
        if (cnpjArray.length === 0) {
            return
        }

        const target = new TheConnectionKeys(this.__parent.app)
        target.companyIds = cnpjArray
        await target.flip()
    }

    async __onCrmExport(connectionId: number) {
        await this.__executePush(connectionId, false)
    }

    private async __executePush(connectionId: number, confirmOverLimit: boolean) {
        const cnpjs = this.__collectSelectedCnpjs()
        if (cnpjs.length === 0) {
            return
        }

        try {
            this.scope.working = true
            const result = await service.pushToCrm({ cnpjs, connectionId, confirmOverLimit })

            if (result.kind === 'franchise_exceeded') {
                this.__pendingPushConnectionId = connectionId
                await this.scope.exportDataLimitScope.onCheckLimitExportData(result.franchise)
                return
            }

            const msg = result.deduped
                ? 'Esta seleção já estava na fila de exportação.'
                : 'Exportação enviada para a fila com sucesso.'
            this.__parent.app.alert('success', 'Exportação para CRM', msg)
        } catch (caught) {
            this.__parent.app.alert('error', 'Exportação para CRM', lang.toError(caught).message)
        } finally {
            this.scope.working = false
            this.scope.update()
        }
    }

    private __collectSelectedCnpjs(): string[] {
        const cnpjArray: string[] = []
        for (const row of this.scope.dataset) {
            if (row.selected) {
                cnpjArray.push(row.cnpj)
            }
        }
        return cnpjArray
    }

    private async __loadCrmExportOptions() {
        const groupId = await service.fetchPreferredGroupId()
        if (groupId == null) {
            this.scope.crmExportOptions = []
            return
        }

        const connections = await service.fetchCrmConnections(groupId)
        this.scope.crmExportOptions = connections.map((c) => ({
            connectionId: c.id,
            crmType: c.crm_type,
            name: c.name
        }))
        this.scope.update()
    }

    override onBeforeScopeUpdate() {
        let checkedRowCount = 0
        for (const row of this.scope.dataset) {
            if (row.selected) {
                checkedRowCount++
            }
        }
        this.scope.checkedRowCount = checkedRowCount
    }
}

const DEFAULT_COLUMNS_VISIBILITY: MRT_VisibilityState = {
    cnpj: true,
    cnes: false,
    nome: true,
    natureza_juridica: false,
    natureza_juridica_grp: false,
    situacao_cadastral_nme: false,
    dta_situacao_cadastral: false,
    dta_inicio_atividade: false,
    cnae: true,
    cnae_descricao: true,
    endereco_cep: false,
    endereco_rua_nome: false,
    endereco_numero: false,
    endereco_complemento: false,
    endereco_bairro_nome: false,
    endereco_cidade_nome: true,
    endereco_uf: true,
    endereco_lat: false,
    endereco_lon: false,
    socios_nomes: false,
    emp_captial_social: false,
    emp_idade_anos: false,
    emp_qtd_grupo: false,
    func_qtd_2020: false,
    func_qtd_2021: false,
    func_qtd_2022: false,
    func_qtd: true,
    func_qtd_masc: false,
    func_qtd_fem: false,
    func_qtd_0_a_18: false,
    func_qtd_19_a_33: false,
    func_qtd_34_a_48: false,
    func_qtd_49_a_58: false,
    func_qtd_59_ou_mais: false,
    faturamento_estimado: true,
    faturamento_estimado_grupo: true,
    matriz_cnpj: false,
    matriz_nome: false,
    matriz_uf: false,
    grupo_cnpj: false,
    telefone1: false,
    telefone2: false,
    email: false,
    salario_total: false,
    salario_total_grupo: false,
    func_qtd_matriz_e_filiais: false,
    qtd_filiais: false,
    web_nome: false,
    web_categoria: false,
    web_nota: false,
    web_qtd_avaliacoes: false,
    web_site: false,
    web_tel: false,
    web_tempo_medio: false,
    optante_simples: false,
    porte_codigo: false
}
